import { ref } from 'vue';
import { WatchfaceService } from '@/services/watchface';
import { useSlotPreview } from './use-slot-preview';
import {
  useImageBufferCache,
  useImageRiveBufferCache,
  useShapeTransform,
  useTextFont,
  useWatchfaceSave
} from '.';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { GlobalConfig } from '@/services/gloabl.config';
import { pickZipExtensions } from '@/utils/fs/zip.utils';
import { pickWatchfacePath } from '@/utils/fs/pick-file.utils';
import { compileAndExportBinFile } from '@/utils/watchface-export';
import { validBinSize } from '@/utils/watchface-export/validBinSize';
import { useLayerStore } from '@/store/use-layer-store';
import { ElectronApi } from '@/services/electron.api';
import { Shape, ShapeType } from '@/services/shape';
import { textToMd5 } from '@/utils/md5.utils';
import { testAccess } from '@/utils/fs/fs-extra.utils';

/**
 * 表盘正在导出状态
 */
export const watchfaceExporting = ref(false);

const _clipTextWatchfaceFont = async () => {
  const path = ElectronApi.path;
  const fs = ElectronApi.fs;
  if (!path || !fs) return;

  // 配置表盘信息
  const layerStore = useLayerStore();
  const { clipFont } = useTextFont();
  const themes = layerStore.themes as StoreThemeItem[];
  const shapes = themes
    .map((theme) => {
      return theme.children.filter(
        (shape) => shape.type == ShapeType.TextWatchface
      );
    })
    .flat()
    .map((item) => Shape.getShape(item.id));

  const service = new WatchfaceService();

  for (const shape of shapes) {
    if (!shape || !shape.usingCustomFont) continue;
    const { customFontFile, texts = [] } = shape.attrs;
    if (!texts.length || !customFontFile) continue;

    const fontPath = path.join(service.ResourcePath, customFontFile);
    const stringWhole = texts.join('\\0') + '' + ' ';
    const textsMd5 = textToMd5(stringWhole, true);

    const customFontFileName = path.basename(customFontFile);
    const customFontCropFile =
      customFontFileName.split('.')?.[0] + '_clip_' + textsMd5 + '.ttf';
    const clipFontPath = path.join(service.ResourcePath, customFontCropFile);
    // 如果文件存在
    if (!(await testAccess(clipFontPath))) {
      await clipFont(fontPath, clipFontPath, stringWhole);
    }
    shape.setAttrs({
      customFontCropFile
    });
  }
};
export const useWatchfaceExport = () => {
  const { generateWidgetPreviewIfNeed } = useSlotPreview();
  const { saveWatchfaceData, editorDataToAst, delUslessResources } =
    useWatchfaceSave();
  const watchfaceStore = useWatchfaceStore();
  const { cacheWholeImages } = useImageBufferCache();
  const { cacheWholeRiveImages } = useImageRiveBufferCache();
  const { generateTransformedImages } = useShapeTransform();

  const exportWatchface = async () => {
    try {
      if (watchfaceExporting.value) {
        console.warn('忙碌，请稍后...');
        return;
      }
      watchfaceExporting.value = true;
      const watchfacePath = watchfaceStore.path;
      console.time('导出耗时');

      // 针对文字表盘优先处理字体裁切
      if (watchfaceStore.watchfaceType == 'textWatchface') {
        console.time('[export-perf] clipTextWatchfaceFont');
        await _clipTextWatchfaceFont();
        console.timeEnd('[export-perf] clipTextWatchfaceFont');
      }
      // 生成预览图（必须放到保存表盘之前）
      console.time('[export-perf] generateWidgetPreviewIfNeed');
      await generateWidgetPreviewIfNeed();
      console.timeEnd('[export-perf] generateWidgetPreviewIfNeed');

      // 生成需要转换的图片
      console.time('[export-perf] generateTransformedImages');
      await generateTransformedImages();
      console.timeEnd('[export-perf] generateTransformedImages');

      // 首先保存表盘信息
      console.time('[export-perf] saveWatchfaceData');
      await saveWatchfaceData('export');
      console.timeEnd('[export-perf] saveWatchfaceData');

      // 处理缓存
      if (GlobalConfig.enablePngConvertCache) {
        console.time('[export-perf] cacheWholeImages');
        await cacheWholeImages();
        await cacheWholeRiveImages();
        console.timeEnd('[export-perf] cacheWholeImages');
      }

      // 生成bin文件
      console.time('[export-perf] compileAndExportBinFile');
      const { binPath, mwzBlob } = await compileAndExportBinFile(
        watchfacePath,
        async () => {
          // 导出成功后，需要重新为app生成一份带有widget翻译的manifest.xml
          const ast2 = await editorDataToAst({ exportForApp: true });
          if (ast2) {
            await new WatchfaceService().manifest.writeManifestXML(ast2);
            await delUslessResources(ast2);
          }
        }
      );
      console.timeEnd('[export-perf] compileAndExportBinFile');      return {
        success: true,
        mwzBlob: mwzBlob,
        binPath,
        overSize: await validBinSize({
          binFile: binPath,
          deleteIfOverSize: true,
          deviceType: watchfaceStore.deviceType,
          maxBinLimit: watchfaceStore.watchfaceDevice?.maxBinLimit
        })
      };
    } catch (error) {
      console.error('exportWatchface错误', error);
      return {
        success: false,
        error: String(error)
      };
    } finally {
      watchfaceExporting.value = false;
      console.timeEnd('导出耗时');
    }
  };

  const pickThenExportWatchface = async (): Promise<
    | {
        success: boolean;
        binPath?: string;
        mwzBlob?: Blob;
        error?: string;
      }
    | undefined
  > => {
    try {
      if (watchfaceExporting.value) {
        console.warn('忙碌，请稍后...');
        return;
      }

      watchfaceExporting.value = true;
      //选择路径
      const { path: watchfacePath } =
        (await pickWatchfacePath('all', pickZipExtensions)) ?? {};
      if (!watchfacePath) return;

      const { mwzBlob, binPath } = await compileAndExportBinFile(watchfacePath);

      return {
        success: true,
        mwzBlob: mwzBlob,
        binPath
      };
    } catch (error) {
      console.error('exportWatchface', error);
      return {
        success: false,
        error: String(error)
      };
    } finally {
      watchfaceExporting.value = false;
    }
  };

  return {
    exportWatchface,
    pickThenExportWatchface,
    watchfaceExporting
  };
};
